import { Grid } from '../../../../../Common/Grid'
import { Button, Input, IconButton } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../../Common/TitleHeader'
import { useNotification } from '../../../../../../../hooks'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  makeCoreGetRequest,
  makeCorePostRequest,
  makeCoreDeleteRequest
} from "../../../../../../../utils/api"
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Paper, CircularProgress } from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import React, { useMemo, useState } from "react"
import { URLS } from '../../../../constants'
import { AsyncAutocomplete } from "../../../../../../core-ui/AsyncAutocomplete"
import { AsyncAutocompleteOption } from "../../../../../../core-ui/AsyncAutocomplete/AsyncAutocompleteDefinitions"

interface DSReturnSupportDTO {
  id: number | null
  supportNumber: number
  idDSSupportType: number
  idDSReturnTruck: number
  supportCode?: string
}

interface DSSupportType {
  id: number
  code: string
  name: string
}

type SaveCtx = {
  prev: DSReturnSupportDTO[] | undefined
  rowId: number | null
}

const ReturnStep3 = () => {
  const { showNotification } = useNotification()
  const location = useLocation()
  const navigate = useNavigate()
  const id = location.state?.id
  const supplierCode: string | undefined = location.state?.supplierCode
  const queryClient = useQueryClient()

  if (!id) {
    navigate(URLS.AUTH_SUPPLIERS_CONTINUE_RETURN)
    return null
  }

  const [newSupportType, setNewSupportType] = useState<AsyncAutocompleteOption | null>(null)
  const [newSupport, setNewSupport] = useState({ idDSSupportType: '', supportNumber: 0 })
  const [maxStockByType, setMaxStockByType] = useState<Record<number, number | null>>({})
  const [newMaxStock, setNewMaxStock] = useState<number | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [editedQty, setEditedQty] = useState<Record<number, number>>({})
  const [savingRow, setSavingRow] = useState<Record<number, boolean>>({})

  const { data: supports, isLoading } = useQuery(['dsReturnSupportList', id], async () => {
    return await makeCoreGetRequest(`/shop-reception-mobile/ds-return-truck/ds-return-support-list/${id}`, null)
  }, {
    onError: (err) =>
      showNotification({
        message: `Eroare la preluarea suporturilor: ${formatErrorMessage(err)}`,
        type: 'error'
      }),
  })

  const { data: supportTypes, isLoading: isLoadingTypes } = useQuery<DSSupportType[]>(
    ['dsSupportTypeListSupplier', supplierCode],
    async () => await makeCoreGetRequest('/shop-reception-mobile/ds-return-truck/ds-support-type-list/supplier', { params: { supplierCode } }),
    {
      retry: 1,
      onError: () => {
        showNotification({
          message: 'Nu s-au putut încărca tipurile de ambalaje. Se vor afișa doar ID-urile.',
          type: 'warning',
        })
      },
    }
  )

  const saveMutation = useMutation<void, unknown, DSReturnSupportDTO, SaveCtx>({
    mutationFn: (payload) =>
      makeCorePostRequest('/shop-reception-mobile/ds-return-truck/ds-return-support-add', payload),

    onMutate: async (payload) => {
      const rowId = payload.id ?? null

      await queryClient.cancelQueries(['dsReturnSupportList', id])
      const prev = queryClient.getQueryData<DSReturnSupportDTO[]>(['dsReturnSupportList', id])

      if (rowId != null) {
        setSavingRow((m) => ({ ...m, [rowId]: true }))
        queryClient.setQueryData<DSReturnSupportDTO[]>(
          ['dsReturnSupportList', id],
          (old) => (old || []).map(r => (r.id === rowId ? { ...r, supportNumber: payload.supportNumber } : r))
        )
      }

      return { prev, rowId }
    },

    onError: (err, _payload, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['dsReturnSupportList', id], ctx.prev)
      showNotification({ message: `Nu s-a putut salva cantitatea. ${formatErrorMessage(err)}`, type: 'error' })
    },

    onSuccess: (_, __, ctx) => {
      showNotification({ message: 'Suport salvat cu succes', type: 'success' })
      if (ctx?.rowId != null) {
        setSavingRow(m => {
          const { [ctx.rowId!]: _, ...rest } = m
          return rest
        })
        setEditedQty(m => {
          const { [ctx.rowId!]: __, ...rest } = m
          return rest
        })
      } else {
        setIsAdding(false)
        setNewSupportType(null)
        setNewSupport({ idDSSupportType: '', supportNumber: 0 })
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries(['dsReturnSupportList', id])
    },
  })

  const deleteMutation = useMutation(
    async (supportId) => makeCoreDeleteRequest(`/shop-reception-mobile/ds-return-truck/ds-return-support-delete/${id}/${supportId}`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['dsReturnSupportList', id])
        showNotification({ message: 'Suport șters', type: 'success' })
      },
      onError: (err) =>
        showNotification({
          message: `Eroare la ștergerea suportului: ${formatErrorMessage(err)}`,
          type: 'error'
        }),
    }
  )

  const finalizeMutation = useMutation(
    async () => makeCorePostRequest(`/shop-reception-mobile/ds-return-truck/ds-finalize-return-truck/${id}`, null),
    {
      onSuccess: (data) => {
        const { noticeSeries, noticeNumber } = data
        navigate(URLS.AUTH_SUPPLIERS_RETURN_STEP_5, { state: { id, noticeSeries, noticeNumber } })
      },
      onError: (err) =>
        showNotification({
          message: `Eroare la finalizarea camionului: ${formatErrorMessage(err)}`,
          type: 'error'
        }),
    }
  )

  const fetchStockForSupportType = async (supportTypeId: number, supportName?: string) => {
    const truck = await makeCoreGetRequest(`/shop-reception-mobile/ds-return-truck/${id}`, null)

    const shopCodeFilter = `[${truck.shopCode}]`
    const supplierCodeFilter = truck.supplierCode

    const typeName =
      supportName ??
      supportTypes?.find(t => t.id === supportTypeId)?.name ?? ''

    const response = await makeCoreGetRequest('/shop-reception/ds-support-stock', {
      params: {
        page: 0,
        size: 1,
        order: 'desc',
        orderBy: 'lastUpdate',
        filterBy: 'shopCode|supplierCode|supportName',
        filterValue: `${shopCodeFilter}|${supplierCodeFilter}|${typeName}`,
      },
    })

    const stock = response?.list?.[0]?.stock
    return typeof stock === 'number' ? stock : null
  }

  const supportTypeMap = useMemo(() => {
    const map = new Map<number, string>()
    supportTypes?.forEach((s) => {
      map.set(s.id, `${s.name} (${s.code})`)
    })
    return map
  }, [supportTypes])

  const handleDelete = (supportId) => deleteMutation.mutate(supportId)

  const handleAddNew = () => {
    if (!newSupport.idDSSupportType) {
      showNotification({ message: 'Selectează un tip de ambalaj', type: 'warning' })
      return
    }

    const qty = Number(newSupport.supportNumber) || 0
    if (qty < 0) {
      showNotification({ message: 'Cantitatea nu poate fi negativă', type: 'warning' })
      return
    }

    if (newMaxStock !== null && qty > newMaxStock) {
      showNotification({
        message: 'Cantitatea introdusă este mai mare decât stocul disponibil.',
        type: 'error'
      })
      return
    }

    saveMutation.mutate({
      id: null,
      idDSSupportType: Number(newSupport.idDSSupportType),
      idDSReturnTruck: id,
      supportNumber: qty,
    })
  }

  const handleQtyChange = async (rowId: number | null, val: number, typeId: number) => {
    if (rowId == null) return

    if (!(typeId in maxStockByType)) {
      try {
        const stock = await fetchStockForSupportType(typeId)
        setMaxStockByType(m => ({ ...m, [typeId]: stock }))
      } catch (err) {
        setMaxStockByType(m => ({ ...m, [typeId]: null }))
      }
    }

    const max = maxStockByType[typeId] ?? null
    if (max !== null && val > max) {
      showNotification({
        message: 'Cantitatea introdusă este mai mare decât stocul disponibil.',
        type: 'error'
      })
      return
    }

    setEditedQty(m => ({ ...m, [rowId]: val }))
  }

  const handleCommit = async (row: DSReturnSupportDTO) => {
    if (row.id == null) return
    const val = editedQty[row.id] ?? row.supportNumber

    let max = maxStockByType[row.idDSSupportType] ?? null
    if (!(row.idDSSupportType in maxStockByType)) {
      try {
        const stock = await fetchStockForSupportType(row.idDSSupportType)
        setMaxStockByType(m => ({ ...m, [row.idDSSupportType]: stock }))
        max = stock
      } catch {
        setMaxStockByType(m => ({ ...m, [row.idDSSupportType]: null }))
      }
    }

    if (max !== null && Number(val) > max) {
      showNotification({
        message: 'Cantitatea introdusă este mai mare decât stocul disponibil.',
        type: 'error'
      })
      return
    }

    saveMutation.mutate({
      id: row.id,
      idDSSupportType: row.idDSSupportType,
      idDSReturnTruck: id!,
      supportNumber: Number(val),
    })
  }

  const supportTypeOptions = useMemo(
    () => supportTypes?.map(support => ({ value: support.id, label: support.code })) || [],
    [supportTypes]
  )

  if (isLoading) return <CircularProgress style={{ margin: '40px auto' }} />

  return (
    <Grid
      gridContainerStyle={{ marginTop: '20px' }}
      itemsPerRow={1}
      items={[
        <TitleHeader title='ÎNREGISTREAZǍ CAMION' />,
        <TableContainer component={Paper}>
          <Table>
            <TableHead style={{ background: '#0970e6' }}>
              <TableRow>
                <TableCell style={{ fontFamily: 'Ubuntu', color: '#fff', fontWeight: 'bold' }}>TIP AMBALAJ</TableCell>
                <TableCell align='center' style={{ fontFamily: 'Ubuntu', color: '#fff', fontWeight: 'bold' }}>CANTITATE</TableCell>
                <TableCell align='center' style={{ color: '#fff', fontWeight: 'bold' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {supports?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" style={{ padding: '20px' }}>
                    Nu există ambalaje adăugate. Apasă "Adaugă ambalaj" pentru a începe.
                  </TableCell>
                </TableRow>
              )}
              {supports?.map((s) => (
                <TableRow key={s.id}>
                  <TableCell align="left">
                    {`${s.supportCode}` || supportTypeMap.get(s.idDSSupportType)}
                  </TableCell>
                  <TableCell align='center'>
                    <div style={{ position: 'relative' }}>
                      <Input
                        type="number"
                        variant="standard"
                        value={s.id != null ? (editedQty[s.id] ?? s.supportNumber) : s.supportNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleQtyChange(s.id, Number(e.target.value || 0), s.idDSSupportType)
                        }
                        onBlur={() => handleCommit(s)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur() }}
                        disabled={s.id != null ? !!savingRow[s.id] : false}
                      />
                      {savingRow[s.id!] && (
                        <span style={{ position: 'absolute', right: -24, top: 6, fontSize: 12 }}>⏳</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton onClick={() => handleDelete(s.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {isAdding && (
                <TableRow>
                  <TableCell>
                    <AsyncAutocomplete
                      variant="outlined"
                      name="searchSupportType"
                      label="Cauta tip suport"
                      value={newSupportType}
                      options={supportTypeOptions}
                      onChange={async (opt) => {
                        setNewSupportType(opt)
                        const typeId = opt ? Number(opt.value) : null
                        setNewSupport(prev => ({ ...prev, idDSSupportType: typeId ? String(typeId) : '' }))

                        if (!typeId) {
                          setNewMaxStock(null)
                          setNewSupport(prev => ({ ...prev, supportNumber: 0 }))
                          return
                        }

                        try {
                          const stock = await fetchStockForSupportType(typeId)
                          setNewMaxStock(stock)

                          if (typeof stock === 'number') {
                            setNewSupport(prev => ({ ...prev, supportNumber: stock }))
                          } else {
                            setNewSupport(prev => ({ ...prev, supportNumber: 0 }))
                          }
                        } catch (err) {
                          setNewMaxStock(null)
                          showNotification({
                            message: `Eroare la preluarea stocului. ${formatErrorMessage(err)}`,
                            type: 'error'
                          })
                        }
                      }}
                      disabled={isLoadingTypes}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Input
                      type='number'
                      variant="standard"
                      placeholder='0'
                      value={newSupport.supportNumber}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = Number(e.target.value || 0)

                        if (newMaxStock !== null && val > newMaxStock) {
                          showNotification({
                            message: 'Cantitatea introdusă este mai mare decât stocul disponibil.',
                            type: 'error'
                          })
                          return
                        }

                        setNewSupport({ ...newSupport, supportNumber: val })
                      }}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Button
                      label='Salvează'
                      variant='contained'
                      onClick={handleAddNew}
                      disabled={saveMutation.isLoading}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>,
        !isAdding && (
          <Button
            type='button'
            variant='outlined'
            label='Adaugă ambalaj'
            onClick={() => setIsAdding(true)}
          />
        ),
        <Button
          type='button'
          variant='contained'
          label='Finalizează camionul'
          onClick={() => finalizeMutation.mutate()}
          disabled={finalizeMutation.isLoading}
          style={{ width: '100%' }}
        />
      ]}
    />
  )
}

export default ReturnStep3
